/**
 * src/ui/escolher.ts — `/escolher`: a decisão "usar este navegador como tela" vs
 * "gerenciar". É uma ROTA real e não um modal porque a decisão é praticamente
 * irreversível para o lojista (o navegador do balcão vira a TV).
 *
 * O que esta tela faz, em ordem (HYBRID §5/§6):
 *  1. lê a cota em `GET /account/plan` e mostra "N de M telas conectadas";
 *  2. sugere um nome para a tela;
 *  3. `POST /screens/self-register` com `Idempotency-Key`;
 *  4. grava a credencial em `simpleindor.screen.v1` e vai para `/tela`.
 *
 * `403 SCREEN_LIMIT_REACHED` vira paywall com `details` + `upgradeUrl` (§3.7).
 *
 * GUARDA (plano §3.8): se já existe `simpleindor.screen.v1`, o self-register
 * NUNCA acontece sem confirmação explícita — sobrescrever deixaria a tela
 * anterior órfã no servidor, consumindo cota do plano para sempre.
 */
import { ApiError, suggestScreenName, type AccountPlan, type CredentialStore, type ScreensClient } from '../../net';

function el(tag: string, className?: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

export interface EscolherOptions {
  /** Onde gravar a credencial da tela — o self-register devolve o device token. */
  credentialStore: CredentialStore;
  /** `true` quando este navegador já é tela — força confirmação antes de trocar. */
  alreadyScreen: boolean;
  onLinkByQr: () => void;
  onDone: (message: string) => void;
}

export function renderEscolher(screens: ScreensClient, options: EscolherOptions): HTMLElement {
  const { credentialStore } = options;
  const root = el('section', 'pagina pagina--escolher');
  root.dataset['papel'] = 'escolher';
  root.append(el('h1', 'pagina__titulo', 'Este navegador vai ser a TV?'));

  const aviso = el('p', 'pagina__aviso');
  aviso.dataset['papel'] = 'aviso-escolher';
  aviso.textContent = options.alreadyScreen
    ? 'Este navegador já exibe anúncios. Criar outra tela deixa a anterior parada na sua conta, ocupando uma vaga do plano.'
    : 'Se virar a TV, este navegador passa a exibir anúncios sozinho. Para cadastrar anúncio, use o app no celular.';
  root.append(aviso);

  const cota = el('p', 'pagina__cota');
  cota.dataset['papel'] = 'cota';
  root.append(cota);

  const cartao = el('div', 'cartao');
  cartao.append(el('h2', 'cartao__titulo', 'Usar este navegador como tela'));

  const nome = document.createElement('input');
  nome.className = 'campo__input';
  nome.type = 'text';
  nome.maxLength = 60;
  nome.placeholder = 'Nome da tela';
  nome.setAttribute('aria-label', 'Nome da tela');
  cartao.append(nome);

  const erro = el('p', 'formulario__erro');
  erro.dataset['papel'] = 'erro';
  erro.hidden = true;
  cartao.append(erro);

  const paywall = el('div', 'paywall');
  paywall.dataset['papel'] = 'paywall';
  paywall.hidden = true;
  cartao.append(paywall);

  const confirmar = el('div', 'confirmacao');
  confirmar.dataset['papel'] = 'confirmacao-sobrescrever';
  confirmar.hidden = true;
  confirmar.append(el('p', 'confirmacao__texto', 'A tela atual para de exibir, mas continua na sua conta ocupando uma vaga. Criar outra mesmo assim?'));
  const botaoConfirmar = document.createElement('button');
  botaoConfirmar.type = 'button';
  botaoConfirmar.className = 'botao botao--primario';
  botaoConfirmar.textContent = 'Sim, criar outra tela';
  const botaoCancelar = document.createElement('button');
  botaoCancelar.type = 'button';
  botaoCancelar.className = 'botao botao--secundario';
  botaoCancelar.textContent = 'Cancelar';
  botaoCancelar.addEventListener('click', () => {
    confirmar.hidden = true;
  });
  confirmar.append(botaoConfirmar, botaoCancelar);
  cartao.append(confirmar);

  const virar = document.createElement('button');
  virar.type = 'button';
  virar.className = 'botao botao--primario';
  virar.textContent = 'Usar este navegador como tela';
  cartao.append(virar);
  root.append(cartao);

  const mostrarPaywall = (error: ApiError): void => {
    const details = (error.details ?? {}) as {
      planName?: string;
      maxScreens?: number;
      upgradeUrl?: string | null;
    };
    paywall.replaceChildren(
      el(
        'p',
        'paywall__texto',
        `Você atingiu o limite de ${details.maxScreens ?? 1} tela(s) do plano ${details.planName ?? ''}.`.trim(),
      ),
    );
    if (details.upgradeUrl) {
      const link = document.createElement('a');
      link.className = 'botao botao--primario';
      link.href = details.upgradeUrl;
      link.textContent = 'Ver planos';
      // `rel=noopener`: a aba nova não ganha acesso a `window.opener`.
      link.target = '_blank';
      link.rel = 'noopener';
      paywall.append(link);
    }
    paywall.hidden = false;
  };

  // A chave é da TENTATIVA: nasce aqui e é reusada em retry/duplo clique, para
  // que uma resposta perdida não crie uma SEGUNDA tela (cota queimada).
  let idempotencyKey: string | null = null;
  const registrar = async (): Promise<void> => {
    cartao.dataset['enviando'] = 'sim';
    virar.disabled = true;
    botaoConfirmar.disabled = true;
    erro.hidden = true;
    paywall.hidden = true;
    idempotencyKey ??= globalThis.crypto?.randomUUID?.() ?? `idem-${Date.now().toString(36)}`;
    const existentes = await screens.list();
    if (!nome.value.trim()) {
      nome.value = suggestScreenName(existentes.length);
    }
    const result = await screens.selfRegister(
      {
        name: nome.value.trim(),
        orientation: globalThis.innerWidth >= globalThis.innerHeight ? 'landscape' : 'portrait',
      },
      idempotencyKey,
    );
    // GRAVA A CREDENCIAL ANTES de navegar: se a aba morrer logo depois da
    // resposta, o device token tem de estar em disco — senão a tela nasce órfã
    // no servidor e o navegador não se reidentifica (HYBRID §5/§6).
    credentialStore.save(result.credentials);
    options.onDone(`Tela "${result.screen.name}" pronta`);
  };

  const falhar = (error: unknown): void => {
    cartao.dataset['enviando'] = 'nao';
    virar.disabled = false;
    botaoConfirmar.disabled = false;
    const api = error as ApiError;
    if (api?.code === 'SCREEN_LIMIT_REACHED') {
      erro.hidden = true;
      mostrarPaywall(api);
      return;
    }
    erro.textContent =
      api?.code === 'NETWORK'
        ? 'Sem conexão com o servidor. Tente de novo.'
        : 'Não foi possível criar a tela agora. Tente de novo.';
    erro.hidden = false;
  };

  const comConfirmacao = (): void => {
    if (options.alreadyScreen && cartao.dataset['confirmado'] !== 'sim') {
      cartao.dataset['confirmado'] = 'sim';
      confirmar.hidden = false;
      return;
    }
    void registrar().catch(falhar);
  };

  virar.addEventListener('click', comConfirmacao);
  botaoConfirmar.addEventListener('click', comConfirmacao);

  const gerenciar = el('div', 'cartao');
  gerenciar.append(el('h2', 'cartao__titulo', 'Só quero gerenciar'));
  gerenciar.append(
    el('p', 'cartao__texto', 'Vê as telas já conectadas e gera o QR de uma TV. Publicar anúncio é no app do celular.'),
  );
  const abrirPainel = document.createElement('button');
  abrirPainel.type = 'button';
  abrirPainel.className = 'botao botao--secundario';
  abrirPainel.textContent = 'Abrir o painel';
  abrirPainel.addEventListener('click', () => globalThis.location?.assign('/painel'));
  gerenciar.append(abrirPainel);
  root.append(gerenciar);

  const tv = document.createElement('button');
  tv.type = 'button';
  tv.className = 'botao botao--secundario';
  tv.textContent = 'Outra TV — mostrar QR para o celular aprovar';
  tv.addEventListener('click', options.onLinkByQr);
  root.append(tv);

  /* --------------------------------------------------------- carga da cota */
  // A cota é sempre lida: a tela pode ter sido criada desde o último boot.
  void screens.getAccountPlan().then(
    (plan: AccountPlan) => {
      cota.textContent = `Você usa ${plan.currentScreens} de ${plan.maxScreens} tela(s) do plano ${plan.planName}.`;
      if (plan.currentScreens >= plan.maxScreens && !options.alreadyScreen) {
        virar.disabled = true;
        virar.title = 'Limite do plano atingido — faça upgrade para adicionar telas.';
      }
      if (!nome.value) {
        nome.value = suggestScreenName(plan.currentScreens);
      }
    },
    () => {
      // Sem plano legível a UI OMITE a cota em vez de inventar número (§2.6).
      cota.textContent = '';
    },
  );

  return root;
}
